import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { HTTPError } from "griptonite-api-client/client";
import { ApiCreditAccountAdmin } from "griptonite-api-client/types";
import { useState } from "react";

import { useChangeCreditAccountStatusMutation } from "@/hooks/credits";
import pluralize from "@/utils/pluralize";

export type RestrictiveStatusAction = "freeze" | "ban";

const COPY: Record<RestrictiveStatusAction, { title: string; confirm: string; failure: string }> = {
  freeze: {
    title: "Freeze",
    confirm: "Freeze account",
    failure: "Couldn’t freeze the account. Please try again.",
  },
  ban: {
    title: "Ban",
    confirm: "Ban account",
    failure: "Couldn’t ban the account. Please try again.",
  },
};

// A 403 or 404 means the viewer's role no longer allows the change, such as an account that was
// banned by someone else since the list loaded.
const statusErrorMessage = (action: RestrictiveStatusAction, error: Error) => {
  if (error instanceof HTTPError && [403, 404].includes(error.response.status)) {
    return "You don’t have permission to make this change.";
  }
  return COPY[action].failure;
};

// Freezes or bans a member's account once the user has confirmed, with the reason they gave.
// Unfreezing needs no confirmation, so it doesn't come through here. Mounted fresh each time it
// opens, so it starts empty.
export default function AccountStatusModal({
  account,
  systemSlug,
  action,
  onClose,
}: {
  account: ApiCreditAccountAdmin;
  systemSlug: string;
  action: RestrictiveStatusAction;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const mutation = useChangeCreditAccountStatusMutation(systemSlug, account.uuid);
  const { title, confirm } = COPY[action];
  const name = account.user.name;

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: "100%", maxWidth: 440 }}>
        <ModalClose />
        <DialogTitle>
          {title} {name}’s account
        </DialogTitle>
        {/* The theme centres dialog text for confirmations; this is a form. */}
        <DialogContent sx={{ textAlign: "left" }}>
          <Stack
            component="form"
            id="account-status-form"
            gap={2}
            sx={{ pt: 1 }}
            onSubmit={(event) => {
              event.preventDefault();
              mutation.mutate({ action, reason: reason.trim() }, { onSuccess: onClose });
            }}
          >
            {action === "freeze" ? (
              <Typography level="body-sm">
                {name} won’t be able to earn or spend credits, or join quests, until the account is
                unfrozen.
              </Typography>
            ) : (
              <Alert color="danger" variant="soft">
                This stops {name} from ever participating in the credit system again. Their balance
                of {pluralize(account.balance, "credit")} will be destroyed.
              </Alert>
            )}
            <FormControl>
              <FormLabel>Reason</FormLabel>
              <Textarea
                placeholder="e.g. Falsely logged routes on 2nd April"
                minRows={2}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </FormControl>
            {mutation.error ? (
              <Alert color="danger">{statusErrorMessage(action, mutation.error)}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            form="account-status-form"
            color="danger"
            loading={mutation.isPending}
          >
            {confirm}
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
